import React, { useEffect, useState } from "react";
import { observer } from "mobx-react";
import dayjs from "dayjs";
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  Grid2,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  alpha,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import CustomTypography from "../../../../../../../views/components/common/form/CustomTypography";
import SkuCopyRow from "../../../../../../../views/components/common/SkuCopyRow";
import CustomTextField from "../../../../../../../views/components/common/textFields/TextField";
import AppTablePagenation from "../../../../../../../views/components/common/pages/AppTablePagenation";
import InlineFormError from "../../../../../../../views/components/common/form/InlineFormError";
import { useDebounce } from "../../../../../../../views/components/common/hooks/Debounce";
import {
  formatDateWithoutTime,
  formatTimeOnly,
} from "../../../../../../../views/components/common/functions/formatDate";
import {
  getModernPageColors,
  modernDrawerHeaderSx,
} from "../../../../../../../styles/modernListingPageSx";
import {
  InventoryLedgerLogType,
  LEDGER_IN_TYPES,
  LEDGER_OUT_TYPES,
  PRODUCT_TRANSACTIONS_PAGE_SIZE,
  ProductTransactionRow,
} from "../../../../../../../models/SwageazyInventory/SwageazyInventoryAdjustment";
import SwageazyInventoryAdjustmentStore from "../../../../../stores/SwageazyInventoryAdjustmentStore";

type LedgerDirection = "in" | "out" | "neutral";

const getDirection = (row: ProductTransactionRow): LedgerDirection => {
  if (LEDGER_IN_TYPES.includes(row.log_type)) return "in";
  if (LEDGER_OUT_TYPES.includes(row.log_type)) return "out";
  return "neutral";
};

type ChipTone = "success" | "error" | "warning" | "info" | "primary" | "secondary";

/**
 * Same tinted-chip palette as the SKU Audit Ledger / Adjustment Audit Logs:
 * green = stock in, red = stock out, amber = manual adjustment,
 * indigo = box transfer, purple = SKU transfer, blue = blocked stock.
 */
const LOG_TYPE_TONE: Record<InventoryLedgerLogType, ChipTone> = {
  credit: "success",
  return: "success",
  debit: "error",
  adjust_in: "warning",
  adjust_out: "warning",
  transfer_in: "primary",
  transfer_out: "primary",
  sku_move_in: "secondary",
  sku_move_out: "secondary",
  block: "info",
  release: "info",
};

const typeChipSx = (tone: ChipTone) => ({
  fontWeight: 600,
  color: `${tone}.dark`,
  bgcolor: (theme: Theme) => alpha(theme.palette[tone].main, 0.12),
  border: (theme: Theme) => `1px solid ${alpha(theme.palette[tone].main, 0.3)}`,
});

/** "transfer_in" → "Transfer In" */
const humanize = (value: string | null | undefined) =>
  (value || "")
    .replace(/[_-]+/g, " ")
    .trim()
    .toLowerCase()
    .replace(/\b\w/g, (ch) => ch.toUpperCase()) || "—";

const ellipsisSx = {
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
} as const;

const clampSx = (lines: number) =>
  ({
    display: "-webkit-box",
    WebkitLineClamp: lines,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    wordBreak: "break-word",
  }) as const;

const formatExpiry = (value: string) =>
  dayjs(value).isValid() ? dayjs(value).format("DD MMM YYYY") : value;

const TransactionTableRow: React.FC<{ row: ProductTransactionRow }> = ({ row }) => {
  const theme = useTheme();
  const colors = getModernPageColors(theme);
  const direction = getDirection(row);
  const qty = Number(row.quantity) || 0;
  const rejected = Number(row.quantity_rejected) || 0;
  const isBoxTransfer = Boolean(row.from_box_id || row.to_box_id);
  const tone = LOG_TYPE_TONE[row.log_type] ?? "info";

  const references = [
    row.pr_id && {
      label: "PR",
      value: `#${row.pr_id}${row.pr_raised_by ? ` · ${row.pr_raised_by}` : ""}`,
    },
    row.po_id && {
      label: "PO",
      value: `#${row.po_id}${row.po_raised_by ? ` · ${row.po_raised_by}` : ""}`,
    },
    row.order_id && { label: "Order", value: `#${row.order_id}` },
    row.expiry_date && { label: "Expiry", value: formatExpiry(row.expiry_date) },
  ].filter(Boolean) as { label: string; value: string }[];

  return (
    <TableRow
      hover
      sx={{
        "& td": { py: 1.25, verticalAlign: "top" },
        "&:last-child td": { borderBottom: 0 },
      }}
    >
      {/* Date & Time */}
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <CustomTypography variant="body2" fontWeight={500} color={colors.title}>
          {formatDateWithoutTime(row.log_date, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </CustomTypography>
        <CustomTypography variant="body3" color={colors.subtitle}>
          {formatTimeOnly(row.log_date)}
        </CustomTypography>
      </TableCell>

      {/* Activity */}
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <Chip
          label={row.activity_type || humanize(row.log_type)}
          size="small"
          sx={typeChipSx(tone)}
        />
        <CustomTypography variant="body3" color={colors.subtitle} sx={{ mt: 0.5 }}>
          Log #{row.log_id}
        </CustomTypography>
      </TableCell>

      {/* SKU */}
      <TableCell sx={{ maxWidth: 220 }}>
        <SkuCopyRow sku={row.sku} emptyFallback="—" fontWeight={600} color={colors.title} />
      </TableCell>

      {/* Location */}
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <CustomTypography variant="body2" color={colors.title}>
          {row.warehouse_id || "—"}
        </CustomTypography>
        <CustomTypography variant="body3" color={colors.subtitle}>
          Box {row.box_id || "—"}
          {row.rack ? ` · Rack ${row.rack}` : ""}
        </CustomTypography>
        {isBoxTransfer && (
          <CustomTypography variant="body3" color={colors.subtitle}>
            Moved: {row.from_box_id ?? "—"} → {row.to_box_id ?? "—"}
          </CustomTypography>
        )}
      </TableCell>

      {/* Quantity */}
      <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
        <CustomTypography
          variant="body2"
          fontWeight={700}
          color={
            direction === "in"
              ? colors.credit
              : direction === "out"
                ? colors.debit
                : colors.title
          }
        >
          {direction === "in" ? "+" : direction === "out" ? "−" : ""}
          {qty}
        </CustomTypography>
        {direction === "neutral" && (
          <CustomTypography variant="body3" color={colors.subtitle}>
            Blocked stock only
          </CustomTypography>
        )}
        {rejected > 0 && (
          <CustomTypography variant="body3" color={colors.debit}>
            {rejected} rejected
          </CustomTypography>
        )}
      </TableCell>

      {/* References */}
      <TableCell sx={{ minWidth: 180, maxWidth: 260 }}>
        {references.length === 0 && !row.rejection_reason ? (
          <CustomTypography variant="body2" color="text.disabled">
            —
          </CustomTypography>
        ) : (
          <>
            {references.map((ref) => (
              <CustomTypography
                key={ref.label}
                variant="body3"
                color={colors.title}
                sx={ellipsisSx}
                title={ref.value}
              >
                <Box component="span" sx={{ color: colors.subtitle, mr: 0.5 }}>
                  {ref.label}
                </Box>
                {ref.value}
              </CustomTypography>
            ))}
            {row.rejection_reason && (
              <CustomTypography
                variant="body3"
                color={colors.debit}
                sx={{ ...clampSx(2), mt: references.length ? 0.25 : 0 }}
                title={row.rejection_reason}
              >
                Rejection: {row.rejection_reason}
              </CustomTypography>
            )}
          </>
        )}
      </TableCell>

      {/* Logged by */}
      <TableCell sx={{ maxWidth: 160 }} title={row.logged_by || undefined}>
        {row.logged_by ? (
          <CustomTypography variant="body2" color={colors.title} sx={ellipsisSx}>
            {row.logged_by}
          </CustomTypography>
        ) : (
          <CustomTypography variant="body2" color="text.disabled">
            —
          </CustomTypography>
        )}
      </TableCell>
    </TableRow>
  );
};

interface SwageazyInventoryTransactionsDrawerProps {
  store: SwageazyInventoryAdjustmentStore;
}

const SwageazyInventoryTransactionsDrawer: React.FC<
  SwageazyInventoryTransactionsDrawerProps
> = ({ store }) => {
  const theme = useTheme();
  const c = theme.palette.customColors;
  const colors = getModernPageColors(theme);
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [skuSearch, setSkuSearch] = useState("");
  const debouncedSku = useDebounce(skuSearch, 400);

  useEffect(() => {
    if (!store.isTransactionsOpen) {
      setSkuSearch("");
      return;
    }
    if ((store.transactionFilters?.sku || "") === debouncedSku.trim()) return;
    store.setTransactionFilters({ sku: debouncedSku.trim() || undefined });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSku, store.isTransactionsOpen]);

  const rows = store.transactionRows;
  const totalIn = rows
    .filter((row) => getDirection(row) === "in")
    .reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);
  const totalOut = rows
    .filter((row) => getDirection(row) === "out")
    .reduce((sum, row) => sum + (Number(row.quantity) || 0), 0);

  const headCellSx = {
    fontWeight: 600,
    fontSize: 12,
    color: colors.subtitle,
    textTransform: "uppercase" as const,
    letterSpacing: 0.4,
    whiteSpace: "nowrap" as const,
    backgroundColor: colors.headerBg,
    py: 1.25,
  };

  const summaryTiles = [
    { label: "Entries", value: `${store.transactionTotalCount}`, color: colors.title },
    {
      label: isPhone ? "In (page)" : "Stock in (this page)",
      value: `+${totalIn}`,
      color: totalIn ? colors.credit : colors.title,
    },
    {
      label: isPhone ? "Out (page)" : "Stock out (this page)",
      value: `−${totalOut}`,
      color: totalOut ? colors.debit : colors.title,
    },
  ];

  return (
    <Drawer
      anchor="right"
      open={store.isTransactionsOpen}
      onClose={store.closeTransactions}
      sx={{
        zIndex: 2100,
        "& .MuiDrawer-paper": { zIndex: 2100 },
      }}
    >
      <Box
        sx={{
          width: { xs: "100vw", sm: 760, md: 1040 },
          maxWidth: "100vw",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          bgcolor: theme.palette.mode === "dark" ? "#0F172A" : colors.pageBg,
        }}
      >
        {/* Header */}
        <Box sx={modernDrawerHeaderSx(theme)}>
          <Box sx={{ minWidth: 0 }}>
            <CustomTypography variant="heading4" color={colors.title}>
              Transactions
            </CustomTypography>
            <CustomTypography
              variant="body2"
              color={colors.subtitle}
              sx={{ mt: 0.5, wordBreak: "break-word" }}
            >
              {store.transactionsTitle || "—"}
              {store.transactionFilters?.product_id
                ? ` · #${store.transactionFilters.product_id}`
                : ""}
            </CustomTypography>
          </Box>
          <IconButton onClick={store.closeTransactions} title="Close">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Filters + summary */}
        <Grid2
          container
          spacing={1.5}
          sx={{ px: { xs: 1.5, sm: 2.5 }, pt: 2, flexShrink: 0 }}
        >
          <Grid2 size={12}>
            <CustomTextField
              placeholder="Filter by SKU"
              value={skuSearch}
              onChange={(e) => setSkuSearch(e.target.value)}
              width="100%"
            />
          </Grid2>
          {summaryTiles.map((tile) => (
            <Grid2 key={tile.label} size={4}>
              <Paper
                elevation={0}
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  border: `1px solid ${c.softCardBorder}`,
                  bgcolor: c.softCardBackground,
                }}
              >
                <CustomTypography
                  variant="body3"
                  color={c.mutedSlateText}
                  sx={ellipsisSx}
                >
                  {tile.label}
                </CustomTypography>
                <CustomTypography variant="heading5" color={tile.color}>
                  {tile.value}
                </CustomTypography>
              </Paper>
            </Grid2>
          ))}
        </Grid2>

        {/* Table */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            px: { xs: 1.5, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <InlineFormError message={store.transactionsError} sx={{ mt: 0 }} />
          {store.isTransactionsLoading ? (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress />
            </Box>
          ) : rows.length === 0 ? (
            !store.transactionsError && (
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  textAlign: "center",
                  border: `1px dashed ${theme.palette.divider}`,
                }}
              >
                <CustomTypography variant="body2" color={colors.subtitle}>
                  No transactions found.
                </CustomTypography>
              </Paper>
            )
          ) : (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                flex: 1,
                minHeight: 0,
                borderRadius: 2,
                border: `1px solid ${colors.border}`,
                bgcolor: colors.surface,
              }}
            >
              <Table stickyHeader size="small" sx={{ minWidth: 960 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Date &amp; Time</TableCell>
                    <TableCell sx={headCellSx}>Activity</TableCell>
                    <TableCell sx={headCellSx}>SKU</TableCell>
                    <TableCell sx={headCellSx}>Location</TableCell>
                    <TableCell sx={headCellSx} align="right">
                      Qty
                    </TableCell>
                    <TableCell sx={headCellSx}>References</TableCell>
                    <TableCell sx={headCellSx}>Logged By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TransactionTableRow key={row.log_id} row={row} />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>

        {store.transactionTotalCount > 0 && store.transactionFilters && (
          <Box
            sx={{
              borderTop: `1px solid ${colors.border}`,
              bgcolor: colors.surface,
              flexShrink: 0,
            }}
          >
            <AppTablePagenation
              fontSize="12px"
              pageNo={store.transactionFilters.page_no}
              limit={PRODUCT_TRANSACTIONS_PAGE_SIZE}
              totalCount={store.transactionTotalCount}
              showRowsPerPageOptions={false}
              showLabelRowsPerPage={false}
              onPageChange={(_event, page) =>
                store.setTransactionFilters({ page_no: page })
              }
              handleRowsChange={() => undefined}
            />
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default observer(SwageazyInventoryTransactionsDrawer);
